import { ENTITLEMENT_LABELS, EntitlementKey } from './entitlement-keys';
import { TargetPlanProfile, targetPlanHasEntitlement } from './plan-entitlements-catalog.util';
import { EntitlementsService } from '../Services/entitlements.service';
import {
  buildSettingViolation,
  SettingViolation,
} from './plan-transition-violation-catalog.util';
import {
  collectAdminControlModeDetails,
  getEmployeeSettingsRecord,
  getEmployeeTrackingMode,
  isAdminControlledTrackingMode,
  isHiddenTrackingMode,
} from './employee-tracking-mode.util';

const BASIC_MIN_SCREENSHOT_MINUTES = 15;

// Removed employeeUsesPersonalizedSettings function

function isPeriodicScreenshotsEnabled(globalSettings: Record<string, unknown>): boolean {
  const periodic = globalSettings.ss_periodic;
  if (periodic === undefined || periodic === null || periodic === '') {
    return true;
  }
  return isTruthySetting(periodic);
}

function formatScreenshotIntervalViolation(captureMinutes: number, minAllowed: number): string {
  return `Periodic screenshot interval below ${minAllowed} minutes (currently every ${captureMinutes} min)`;
}

function isScreenshotIntervalViolation(
  globalSettings: Record<string, unknown>,
  targetProfile: TargetPlanProfile
): string | null {
  if (!isPeriodicScreenshotsEnabled(globalSettings)) {
    return null;
  }

  let minAllowed = BASIC_MIN_SCREENSHOT_MINUTES;
  const planCodeLower = String(targetProfile.planCode).toLowerCase();
  if (planCodeLower === 'business') {
    minAllowed = 5;
  } else if (planCodeLower === 'professional') {
    minAllowed = 10;
  }

  const captureMinutes = parseDurationToMinutes(globalSettings.ss_screen_capture_time, BASIC_MIN_SCREENSHOT_MINUTES);
  if (captureMinutes > 0 && captureMinutes < minAllowed) {
    return formatScreenshotIntervalViolation(captureMinutes, minAllowed);
  }
  return null;
}

export function isTruthySetting(value: unknown): boolean {
  if (value === true || value === 1) {
    return true;
  }
  const normalized = String(value ?? '').trim().toLowerCase();
  return normalized === 'true' || normalized === '1' || normalized === 'yes';
}

export function parseDurationToMinutes(value: unknown, fallbackMinutes = 0): number {
  if (value === null || value === undefined || value === '') {
    return fallbackMinutes;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  const raw = String(value).trim();
  if (!raw) {
    return fallbackMinutes;
  }
  const parts = raw.split(':').map((part) => Number(part));
  if (parts.some((part) => Number.isNaN(part))) {
    const asNumber = Number(raw);
    return Number.isFinite(asNumber) ? asNumber : fallbackMinutes;
  }
  if (parts.length >= 2) {
    // HH:MM[:SS] — same as user-setting-panel getMinutesFromTime()
    return parts[0] * 60 + parts[1];
  }
  if (parts.length === 1) {
    return parts[0];
  }
  return fallbackMinutes;
}

export function normalizeGlobalSettings(raw: unknown): Record<string, unknown> {
  if (!raw || typeof raw !== 'object') {
    return {};
  }
  const record = raw as Record<string, unknown>;
  const nested = record.data;
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    return nested as Record<string, unknown>;
  }
  return record;
}

/** Same global settings source the Employees table uses for Admin vs Employee Controlled. */
export function resolveGlobalSettingsForModeDetection(
  apiSettings: unknown,
  cachedSettings: unknown
): Record<string, unknown> {
  const api = normalizeGlobalSettings(apiSettings);
  const cached = normalizeGlobalSettings(cachedSettings);
  if (Object.keys(cached).length) {
    return { ...cached, ...api };
  }
  return api;
}

export function getEmployeeSettings(user: any): Record<string, unknown> {
  return getEmployeeSettingsRecord(user);
}

export function resolveEffectiveSettings(
  user: any,
  globalSettings: Record<string, unknown>
): Record<string, unknown> {
  const settings = user?.meta?.settings;
  if (Array.isArray(settings)) {
    return globalSettings;
  }
  if (settings && typeof settings === 'object') {
    return { ...globalSettings, ...settings };
  }
  return globalSettings;
}

export function isHiddenModeEmployee(
  user: any,
  globalSettings: Record<string, unknown>
): boolean {
  return isHiddenTrackingMode(user, globalSettings);
}

export function getEmployeeVersionType(user: any): string {
  const candidates = [
    user?.version_type,
    user?.meta?.version_type,
    user?.meta?.agent_version_type,
  ];

  for (const candidate of candidates) {
    const normalized = String(candidate ?? '').trim().toLowerCase();
    if (normalized === 'general' || normalized === 'g') return 'general';
    if (normalized === 'silent' || normalized === 'hidden' || normalized === 's') return 'silent';
    if (normalized === 'restricted' || normalized === 'r') return 'restricted';
  }

  const appType = String(user?.appInfo?.applicationType ?? '').trim().toLowerCase();
  if (appType === 'general' || appType === 'silent' || appType === 'restricted') {
    return appType;
  }

  const label = String(user?.appInfo?.label ?? '').trim().toUpperCase();
  if (label === 'G') return 'general';
  if (label === 'S') return 'silent';
  if (label === 'R') return 'restricted';

  return '';
}

export function isAdminControlledEmployee(
  user: any,
  globalSettings: Record<string, unknown>
): boolean {
  return isAdminControlledTrackingMode(user, globalSettings);
}

export function collectAdminControlDetails(
  user: any,
  globalSettings: Record<string, unknown>
): string[] {
  return collectAdminControlModeDetails(user, globalSettings);
}

export function collectEmployeeSettingViolations(
  user: any,
  globalSettings: Record<string, unknown>,
  targetProfile: TargetPlanProfile,
  entitlementsService?: EntitlementsService
): string[] {
  const effective = resolveEffectiveSettings(user, globalSettings);
  const userSettings = getEmployeeSettingsRecord(user);
  const violations: string[] = [];
  const trackingMode = getEmployeeTrackingMode(user, globalSettings);

  if (
    !targetPlanHasEntitlement(targetProfile, EntitlementKey.hidden_mode) &&
    trackingMode === 'hidden'
  ) {
    violations.push('Hidden Mode enabled');
  }

  if (
    !targetPlanHasEntitlement(targetProfile, EntitlementKey.advanced_tracking_controls) &&
    userSettings.ss_strict_time_tracking !== undefined &&
    isTruthySetting(userSettings.ss_strict_time_tracking)
  ) {
    violations.push('Strict time tracking enabled');
  }



  if (userSettings.ss_screen_capture_time !== undefined || userSettings.ss_periodic !== undefined) {
    const intervalViolation = isScreenshotIntervalViolation(
      {
        ...globalSettings,
        ss_screen_capture_time: userSettings.ss_screen_capture_time ?? globalSettings.ss_screen_capture_time,
        ss_periodic: userSettings.ss_periodic ?? globalSettings.ss_periodic,
      },
      targetProfile
    );
    if (intervalViolation) {
      violations.push(intervalViolation);
    }
  }

  const allowsAdvancedModes =
    String(targetProfile.planCode).toLowerCase() !== 'basic';

  if (!allowsAdvancedModes && userSettings.ss_appchange !== undefined && isTruthySetting(userSettings.ss_appchange)) {
    violations.push('App-switch screenshots enabled');
  }

  if (!allowsAdvancedModes && userSettings.ss_webcam !== undefined && isTruthySetting(userSettings.ss_webcam)) {
    if (!entitlementsService || !entitlementsService.hasCompany(EntitlementKey.webcam_capture)) {
      violations.push('Webcam capture enabled');
    }
  }

  const shiftId = user?.meta?.current_shift_id ?? user?.meta?.shift_id;
  if (
    targetProfile.shiftsMax === 1 &&
    shiftId !== null &&
    shiftId !== undefined &&
    String(shiftId).trim() !== ''
  ) {
    violations.push('Custom shift assigned');
  }

  return violations;
}

export function collectGlobalSettingViolations(
  globalSettings: Record<string, unknown>,
  targetProfile: TargetPlanProfile,
  entitlementsService: EntitlementsService,
  employees: any[] = []
): SettingViolation[] {
  const violations: SettingViolation[] = [];
  const inheritedCount = employees.length > 0 ? employees.length : undefined;

  function pushViolation(code: string, label: string, assignCount = false) {
    const v = buildSettingViolation(code, label);
    if (assignCount && inheritedCount !== undefined) {
      v.affectedCount = inheritedCount;
    }
    violations.push(v);
  }

  if (
    !targetPlanHasEntitlement(targetProfile, EntitlementKey.advanced_tracking_controls) &&
    isTruthySetting(globalSettings.ss_appchange)
  ) {
    pushViolation('app_switch', 'App-switch screenshots enabled', true);
  }

  if (
    !targetPlanHasEntitlement(targetProfile, EntitlementKey.advanced_tracking_controls) &&
    isTruthySetting(globalSettings.ss_webcam) &&
    !entitlementsService.hasCompany(EntitlementKey.webcam_capture)
  ) {
    pushViolation('webcam', 'Webcam capture enabled', true);
  }

  if (
    !targetPlanHasEntitlement(targetProfile, EntitlementKey.advanced_tracking_controls) &&
    isTruthySetting(globalSettings.ss_strict_time_tracking)
  ) {
    pushViolation('strict_tracking', 'Strict time tracking enabled', true);
  }



  if (
    !targetPlanHasEntitlement(targetProfile, EntitlementKey.hidden_mode) &&
    isTruthySetting(globalSettings.ss_silent)
  ) {
    pushViolation('hidden_mode', `${ENTITLEMENT_LABELS[EntitlementKey.hidden_mode]} enabled`, true);
  }

  const globalIntervalViolation = isScreenshotIntervalViolation(globalSettings, targetProfile);
  if (globalIntervalViolation) {
    pushViolation('periodic_interval', globalIntervalViolation, true);
  }

  if (
    entitlementsService.canAccessWorkReplay() &&
    !entitlementsService.isWorkReplayPreviewOnly() &&
    !targetPlanHasEntitlement(targetProfile, EntitlementKey.work_replay)
  ) {
    pushViolation('work_replay', `${ENTITLEMENT_LABELS[EntitlementKey.work_replay]} enabled`);
  }

  if (
    entitlementsService.hasCompany(EntitlementKey.productivity_trends) &&
    !targetPlanHasEntitlement(targetProfile, EntitlementKey.productivity_trends)
  ) {
    pushViolation('productivity_trends', `${ENTITLEMENT_LABELS[EntitlementKey.productivity_trends]} enabled`);
  }

  if (
    entitlementsService.hasCompany(EntitlementKey.reports_advanced_filters) &&
    !targetPlanHasEntitlement(targetProfile, EntitlementKey.reports_advanced_filters)
  ) {
    pushViolation('advanced_filters', `${ENTITLEMENT_LABELS[EntitlementKey.reports_advanced_filters]} enabled`);
  }

  if (
    entitlementsService.hasCompany(EntitlementKey.executive_reports) &&
    !targetPlanHasEntitlement(targetProfile, EntitlementKey.executive_reports)
  ) {
    pushViolation('executive_reports', `${ENTITLEMENT_LABELS[EntitlementKey.executive_reports]} enabled`);
  }

  if (
    entitlementsService.hasCompany(EntitlementKey.extended_retention) &&
    !targetPlanHasEntitlement(targetProfile, EntitlementKey.extended_retention)
  ) {
    pushViolation('extended_retention', `${ENTITLEMENT_LABELS[EntitlementKey.extended_retention]} active`);
  }

  return violations;
}

export function settingViolationLabels(violations: SettingViolation[]): string[] {
  return violations.map((violation) => violation.label);
}
